import { Layers } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { formatHoursToTimeString } from '@/helpers';
import { cn, normalizeColorHex } from '@/lib/utils';

import { getProgressColor } from '../../helpers/progress-color.helper';
import { calculateProgressPercentage } from '../../helpers/progress-percentage.helper';

/** Fallback when a project doesn't carry a color hex. zinc-500 from the design tokens. */
const PROJECT_DOT_FALLBACK_COLOR = '#71717A';

export interface ProjectBreakdownRowItem {
  projectId: number;
  projectName: string;
  colorHex: string | null;
  trackedHours: number;
  bookedHours: number;
  /** When true, renders an inline background-work badge after the project name. */
  isBackgroundWork?: boolean;
}

interface ProjectBreakdownRowProps {
  item: ProjectBreakdownRowItem;
  /** When true, wraps the row in a bordered card (used inside the day-detail panel). */
  boxed?: boolean;
  /** When true, indents the progress bar to align with the row content (used in the projects-this-month list). */
  indented?: boolean;
}

/**
 * Single project row used by both the day-detail panel and the "Projects this
 * month" card. Renders: project color dot + name + tracked hours + "of Xh booked"
 * + a trafficlight progress bar.
 */
export function ProjectBreakdownRow({
  item,
  boxed = false,
  indented = false,
}: ProjectBreakdownRowProps) {
  const { t } = useTranslation('authenticated.timeTracking');

  const dotColor = normalizeColorHex(item.colorHex) ?? PROJECT_DOT_FALLBACK_COLOR;
  const progressPercentage = calculateProgressPercentage(item.trackedHours, item.bookedHours);
  const barColor = getProgressColor(progressPercentage);
  const widthPct =
    item.bookedHours > 0
      ? Math.min(100, (item.trackedHours / item.bookedHours) * 100)
      : item.trackedHours > 0
        ? 100
        : 0;

  return (
    <div className={cn('flex flex-col gap-1.5', boxed && 'rounded-md border p-2.5')}>
      <div className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: dotColor }}
        />
        <span className="min-w-0 flex-1 truncate text-[13px] font-medium">{item.projectName}</span>
        {item.isBackgroundWork && (
          <SimpleTooltip content={t('weekView.cell.backgroundWorkTooltip')}>
            <span
              aria-label={t('weekView.cell.backgroundWorkBadgeAriaLabel')}
              className="flex h-4 w-4 shrink-0 items-center justify-center rounded-sm bg-foreground/10 text-foreground/70"
            >
              <Layers className="h-3 w-3" />
            </span>
          </SimpleTooltip>
        )}
        <span className="text-[13px] font-semibold tabular-nums">
          {formatHoursToTimeString(item.trackedHours)}
        </span>
        {item.bookedHours > 0 && (
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {t('monthView.detail.ofBookedShort', {
              booked: formatHoursToTimeString(item.bookedHours),
            })}
          </span>
        )}
      </div>
      <div className={cn('h-1 overflow-hidden rounded-full bg-track-empty', indented && 'ml-4')}>
        <div
          className="h-full rounded-full"
          style={{ width: `${widthPct}%`, backgroundColor: barColor }}
        />
      </div>
    </div>
  );
}
